<template>
  <div class="diaply-container">
    <!-- Background vignette -->
    <div class="vignette" aria-hidden="true"></div>

    <!-- Particles -->
    <div class="particles" aria-hidden="true">
      <span v-for="n in 24" :key="n" class="particle"
        :style="{
          left: `${(n * 41 + 7) % 100}%`,
          top: `${(n * 59 + 13) % 100}%`,
          animationDelay: `${(n * 0.6) % 5}s`,
          animationDuration: `${4 + (n % 4)}s`,
          width: `${2 + (n % 3)}px`,
          height: `${2 + (n % 3)}px`,
        }"
      ></span>
    </div>

    <!-- Header -->
    <header class="lb-header">
      <div class="header-line"></div>
      <div class="header-title">
        <span class="live-dot" aria-hidden="true"></span>
        <h1>Latest Bids</h1>
        <span class="live-label">LIVE</span>
      </div>
      <div class="header-line"></div>
    </header>

    <div class="scroll-container" ref="scrollContainer">
      <transition-group name="bid" tag="ul" class="bids-list">
        <li
          v-for="(bid, index) in latestBids"
          :key="bid.id"
          class="bid-block"
          :class="{ 'is-newest': index === 0 }"
        >
          <span class="accent-bar" aria-hidden="true"></span>
          <div class="bid-number-wrap">
            <span class="bid-number-label">LOT</span>
            <span class="bid-number">{{ bid.itemNumber }}</span>
          </div>
          <div class="bid-image">
            <img :src="bid.itemImage" :alt="bid.itemName" />
          </div>
          <div class="bid-content">
            <span class="bid-itemname">{{ bid.itemName }}</span>
            <span class="bid-username">{{ bid.user.name }}</span>
          </div>
          <div class="bid-amount-wrap">
            <span class="bid-amount-label">Current Bid</span>
            <span
              class="bid-amount"
              v-html="formatCurrency(bid.userBid, bid.currency)"
            ></span>
          </div>
        </li>
      </transition-group>
    </div>

    <Notification
      :message="currentNotification"
      :show="showNotification"
      @hidden="handleNotificationHidden"
    />
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, nextTick } from "vue";
import { db } from "@/firebase";
import {
  collection,
  collectionGroup,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  onSnapshot,
} from "firebase/firestore";
import Notification from "@/views/display/components/Notification.vue";
import { formatPrice } from "@/services/formatPrice";

export default {
  name: "LatestBidsGalaView",
  components: { Notification },
  setup() {
    const latestBids = ref([]);
    const latestBid = ref(null);
    const scrollContainer = ref(null);
    let rafId = null;
    let lastTimestamp = null;
    let pauseEnd = 0;
    let scrollAccum = 0;
    const SCROLL_SPEED = 25; // px/s — ~1/3 faster than the old ~16.7 px/s
    const PAUSE_DURATION = 5000;

    const notificationQueue = ref([]);
    const currentNotification = ref("");
    const showNotification = ref(false);
    const pageLoadTimestamp = new Date();
    const processedBids = new Set();
    const processedDonationDocs = new Set();
    const listenedDonationItems = new Set();
    const sponsorDefaultItemIds = new Set();

    const fetchSponsorDefaultIds = async () => {
      const q = query(
        collection(db, "auctionItems"),
        where("isSponsorDefault", "==", true)
      );
      const snapshot = await getDocs(q);
      snapshot.docs.forEach((doc) => sponsorDefaultItemIds.add(doc.id));
    };

    const formatCurrency = (value, currencyCode = "SEK") => {
      const formattedValue = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 0,
      }).format(value);
      return `${formattedValue} <small>${currencyCode}</small>`;
    };

    const showQueuedNotification = () => {
      if (!showNotification.value && notificationQueue.value.length > 0) {
        currentNotification.value = notificationQueue.value[0];
        showNotification.value = true;
        stopAutoScroll();
      }
    };

    const setupPurchaseListener = (itemId) => {
      if (listenedDonationItems.has(itemId)) return;
      listenedDonationItems.add(itemId);

      const purchasesRef = collection(db, `auctionItems/${itemId}/purchase`);
      const purchasesQuery = query(purchasesRef, orderBy("timestamp", "desc"));

      onSnapshot(purchasesQuery, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type !== "added") return;
          if (processedDonationDocs.has(change.doc.id)) return;
          processedDonationDocs.add(change.doc.id);

          const donation = change.doc.data();
          const donationTime = donation.timestamp?.toDate
            ? donation.timestamp.toDate()
            : new Date((donation.timestamp?.seconds || 0) * 1000);

          if (donationTime <= pageLoadTimestamp) return;

          const formattedPrice = formatPrice(donation.userBid, donation.currency || "SEK");
          const notificationMessage = `
            <h2>NEW DONATION!</h2>
            <h3><span class="stronger">${formattedPrice}</span> by ${donation.user?.name || "Unknown"}</h3>
          `;
          notificationQueue.value.push(notificationMessage);

          nextTick(showQueuedNotification);
        });
      });
    };

    const listenForDonations = () => {
      const donationItemsQuery = query(
        collection(db, "auctionItems"),
        where("isDonationItem", "==", true)
      );

      onSnapshot(donationItemsQuery, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === "added") {
            setupPurchaseListener(change.doc.id);
          }
        });
      });
    };

    const fetchLatestBids = () => {
      const q = query(
        collectionGroup(db, "bids"),
        orderBy("timestamp", "desc"),
        limit(50)
      );

      // Track highest known bid amount per item to avoid duplicate notifications
      const highestKnownBid = {};

      onSnapshot(q, (querySnapshot) => {
        const allBids = querySnapshot.docs
          .filter((doc) => {
            const parentId = doc.ref.parent.parent?.id;
            return !parentId || !sponsorDefaultItemIds.has(parentId);
          })
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .sort((a, b) => {
            const tsA = a.timestamp?.seconds || 0;
            const tsB = b.timestamp?.seconds || 0;
            if (tsB !== tsA) return tsB - tsA;
            return (b.userBid || 0) - (a.userBid || 0);
          });

        // Deduplicate: remove entries where same item has same bid amount
        // (max-bid update with no actual price change)
        const seenItemAmounts = new Set();
        const bids = allBids.filter((bid) => {
          const key = `${bid.itemID}_${bid.userBid}`;
          if (seenItemAmounts.has(key)) return false;
          seenItemAmounts.add(key);
          return true;
        });

        latestBids.value = bids;

        // Notify only when a bid actually raises the displayed price on an item
        allBids.forEach((bid) => {
          if (processedBids.has(bid.id)) return;
          processedBids.add(bid.id);

          const bidTime = bid.timestamp?.toDate ? bid.timestamp.toDate() : new Date(bid.timestamp?.seconds * 1000 || 0);
          if (bidTime <= pageLoadTimestamp) return;

          const currentHighest = highestKnownBid[bid.itemID] || 0;
          if ((bid.userBid || 0) <= currentHighest) return; // max-bid update, price didn't rise
          highestKnownBid[bid.itemID] = bid.userBid;

          const formattedPrice = formatPrice(bid.userBid, bid.currency || "SEK");
          const notificationMessage = `
            <h2>#${bid.itemNumber} - ${bid.itemName}</h2>
            <h3>New bid of <span class="stronger">${formattedPrice}</span> placed by ${bid.user?.name || 'Unknown'}</h3>
          `;
          notificationQueue.value.push(notificationMessage);

          // Show popup; scroll will resume from current position afterwards.
          // New bids always sit at the top of the list, so they'll be visible
          // when the scroll loop naturally resets at the bottom.
          nextTick(() => {
            showQueuedNotification();
          });
        });
      });
    };

    const performScroll = (timestamp) => {
      rafId = requestAnimationFrame(performScroll);
      const container = scrollContainer.value;
      if (!container) return;

      if (timestamp < pauseEnd) {
        lastTimestamp = null;
        return;
      }

      if (!lastTimestamp) {
        lastTimestamp = timestamp;
        return;
      }

      const elapsed = timestamp - lastTimestamp;
      lastTimestamp = timestamp;
      scrollAccum += (elapsed / 1000) * SCROLL_SPEED;
      const pixels = Math.floor(scrollAccum);

      if (pixels > 0) {
        scrollAccum -= pixels;
        if (container.scrollTop + container.clientHeight >= container.scrollHeight - 1) {
          // Reached the bottom — pause then reset to top
          container.scrollTop = 0;
          lastTimestamp = null;
          pauseEnd = timestamp + PAUSE_DURATION;
        } else {
          container.scrollTop += pixels;
        }
      }
    };

    const startAutoScroll = (delay = 3000) => {
      stopAutoScroll();
      scrollAccum = 0;
      lastTimestamp = null;
      pauseEnd = performance.now() + delay;
      rafId = requestAnimationFrame(performScroll);
    };

    const stopAutoScroll = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    const handleNotificationHidden = () => {
      showNotification.value = false;
      notificationQueue.value.shift();
      if (notificationQueue.value.length > 0) {
        setTimeout(() => {
          currentNotification.value = notificationQueue.value[0];
          showNotification.value = true;
        }, 500);
      } else {
        currentNotification.value = "";
        // Resume scrolling from the current position (no reset to top).
        startAutoScroll(0);
      }
    };

    onMounted(async () => {
      await fetchSponsorDefaultIds();
      fetchLatestBids();
      listenForDonations();
      // Start scrolling immediately on page load
      startAutoScroll(0);
    });

    onBeforeUnmount(() => {
      stopAutoScroll();
    });

    return {
      latestBids,
      latestBid,
      scrollContainer,
      formatCurrency,
      currentNotification,
      showNotification,
      handleNotificationHidden,
    };
  },
};
</script>

<style scoped>
/* ── Base ─────────────────────────────────────────────────────── */
.diaply-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 100vh;
  background:
    radial-gradient(ellipse at top, #0f1a3d 0%, #07102a 55%, #030612 100%);
  color: #fff;
  position: relative;
  overflow: hidden;
  padding: 3vh 0 2vh;
  box-sizing: border-box;
  font-family: "Inter", "Helvetica Neue", Arial, sans-serif;
}

/* Subtle gold inner vignette */
.vignette {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse at center, transparent 55%, rgba(0, 0, 0, 0.55) 100%);
  pointer-events: none;
  z-index: 0;
}

/* ── Particles ─────────────────────────────────────────────────── */
.particles {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}
.particle {
  position: absolute;
  border-radius: 50%;
  background: #c39935;
  box-shadow: 0 0 6px #c39935;
  animation: float-p linear infinite;
}
@keyframes float-p {
  0%   { transform: translateY(0) scale(1);   opacity: 0.25; }
  50%  { transform: translateY(-40px) scale(1.4); opacity: 0.75; }
  100% { transform: translateY(0) scale(1);   opacity: 0.25; }
}

/* ── Header ────────────────────────────────────────────────────── */
.lb-header {
  display: flex;
  align-items: center;
  gap: 32px;
  width: 90vw;
  max-width: 1600px;
  z-index: 1;
  margin-bottom: 3.5vh;
  flex-shrink: 0;
}
.header-line {
  flex: 1;
  height: 2px;
  background: linear-gradient(90deg, transparent, #c39935 50%, transparent);
  box-shadow: 0 0 8px rgba(195, 153, 53, 0.4);
}
.header-title {
  display: flex;
  align-items: center;
  gap: 18px;
}
.live-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #ff3b3b;
  box-shadow: 0 0 18px rgba(255, 59, 59, 0.9);
  animation: live-pulse 1.6s ease-in-out infinite;
}
@keyframes live-pulse {
  0%, 100% { transform: scale(1);   opacity: 1; }
  50%      { transform: scale(1.3); opacity: 0.6; }
}
.live-label {
  font-size: clamp(0.9rem, 1.1vw, 1.2rem);
  font-weight: 800;
  letter-spacing: 0.3em;
  color: #ff5b5b;
  padding: 4px 12px;
  border: 1.5px solid rgba(255, 91, 91, 0.6);
  border-radius: 4px;
  text-shadow: 0 0 10px rgba(255, 59, 59, 0.6);
}
h1 {
  font-family: "Playfair Display", serif !important;
  font-size: clamp(2.8rem, 5.2vw, 5rem);
  font-weight: 400;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #c39935 !important;
  white-space: nowrap;
  margin: 0;
  text-shadow:
    0 0 30px rgba(195, 153, 53, 0.55),
    0 0 60px rgba(195, 153, 53, 0.25);
}

/* ── Scroll container ──────────────────────────────────────────── */
.scroll-container {
  width: 90vw;
  max-width: 1600px;
  flex: 1;
  overflow: hidden;
  position: relative;
  z-index: 1;
  mask-image: linear-gradient(
    180deg,
    transparent 0%,
    #000 4%,
    #000 96%,
    transparent 100%
  );
  -webkit-mask-image: linear-gradient(
    180deg,
    transparent 0%,
    #000 4%,
    #000 96%,
    transparent 100%
  );
}

/* ── List ──────────────────────────────────────────────────────── */
.bids-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.bid-block {
  position: relative;
  display: flex;
  align-items: center;
  gap: clamp(20px, 2.2vw, 40px);
  background:
    linear-gradient(135deg, rgba(195, 153, 53, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%);
  border: 1px solid rgba(195, 153, 53, 0.3);
  border-radius: 16px;
  padding: clamp(18px, 2vh, 28px) clamp(24px, 2.5vw, 40px);
  padding-left: clamp(32px, 3vw, 52px);
  margin-bottom: 18px;
  box-shadow:
    0 6px 24px rgba(0, 0, 0, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(8px);
  overflow: hidden;
  transition: border-color 0.3s ease, transform 0.3s ease;
}

/* Left gold accent bar */
.accent-bar {
  position: absolute;
  left: 0;
  top: 12%;
  bottom: 12%;
  width: 5px;
  border-radius: 0 4px 4px 0;
  background: linear-gradient(180deg, #e4c066, #c39935 50%, #8a6b17);
  box-shadow: 0 0 14px rgba(195, 153, 53, 0.6);
}

/* Right gold accent bar — symmetry */
.bid-block::after {
  content: "";
  position: absolute;
  right: 0;
  top: 12%;
  bottom: 12%;
  width: 5px;
  border-radius: 4px 0 0 4px;
  background: linear-gradient(180deg, #e4c066, #c39935 50%, #8a6b17);
  box-shadow: 0 0 14px rgba(195, 153, 53, 0.6);
  pointer-events: none;
}

/* Highlight newest bid */
.bid-block.is-newest {
  border-color: rgba(195, 153, 53, 0.65);
  background:
    linear-gradient(135deg, rgba(195, 153, 53, 0.16) 0%, rgba(195, 153, 53, 0.04) 100%);
  box-shadow:
    0 8px 36px rgba(195, 153, 53, 0.25),
    0 6px 24px rgba(0, 0, 0, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
}
.bid-block.is-newest .accent-bar,
.bid-block.is-newest::after {
  top: 0;
  bottom: 0;
  width: 6px;
  animation: accent-glow 2s ease-in-out infinite;
}
@keyframes accent-glow {
  0%, 100% { box-shadow: 0 0 14px rgba(195, 153, 53, 0.6); }
  50%      { box-shadow: 0 0 28px rgba(195, 153, 53, 1); }
}

/* ── Lot number badge ──────────────────────────────────────────── */
.bid-number-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: clamp(90px, 8vw, 130px);
  padding: clamp(10px, 1vh, 16px) clamp(8px, 1vw, 14px);
  border: 2px solid rgba(195, 153, 53, 0.5);
  border-radius: 12px;
  background: rgba(195, 153, 53, 0.08);
  flex-shrink: 0;
}
.bid-number-label {
  font-size: clamp(0.7rem, 0.85vw, 0.95rem);
  font-weight: 700;
  letter-spacing: 0.25em;
  color: rgba(195, 153, 53, 0.85);
  text-transform: uppercase;
  margin-bottom: 2px;
}
.bid-number {
  font-family: "Playfair Display", serif;
  font-size: clamp(2.4rem, 3.6vw, 3.8rem);
  font-weight: 700;
  line-height: 1.15;
  padding-bottom: 0.05em;
  color: #e4c066;
  text-shadow: 0 0 18px rgba(195, 153, 53, 0.7);
}

/* ── Image ─────────────────────────────────────────────────────── */
.bid-image {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}
.bid-image img {
  width: clamp(140px, 14vw, 220px);
  height: clamp(100px, 10vw, 160px);
  object-fit: cover;
  border-radius: 10px;
  border: 2px solid rgba(195, 153, 53, 0.5);
  box-shadow:
    0 6px 20px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.03) inset;
}

/* ── Content (item name + user) ────────────────────────────────── */
.bid-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
}
.bid-itemname {
  font-family: "Playfair Display", serif;
  font-style: italic;
  font-size: clamp(1.2rem, 1.6vw, 1.8rem);
  color: rgba(255, 255, 255, 0.65);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.02em;
}
.bid-username {
  font-size: clamp(1.8rem, 2.4vw, 2.8rem);
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.01em;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);
}

/* ── Bid amount ────────────────────────────────────────────────── */
.bid-amount-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  flex-shrink: 0;
  padding-left: clamp(16px, 2vw, 32px);
  border-left: 1px solid rgba(195, 153, 53, 0.25);
}
.bid-amount-label {
  font-size: clamp(0.75rem, 0.95vw, 1rem);
  font-weight: 700;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: rgba(195, 153, 53, 0.75);
  margin-bottom: 4px;
}
.bid-amount {
  font-family: "Playfair Display", serif;
  font-size: clamp(2.2rem, 3.4vw, 4rem);
  font-weight: 700;
  line-height: 1.2;
  padding-bottom: 0.08em;
  background: linear-gradient(135deg, #fff4d1 0%, #e4c066 40%, #c39935 100%);
  background-size: 200% 100%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0 0 18px rgba(195, 153, 53, 0.4));
  animation: shimmer 6s ease-in-out infinite;
  white-space: nowrap;
}
.bid-amount :deep(small) {
  /* Reset gradient text so the currency code stays visible */
  -webkit-text-fill-color: #c39935;
  color: #c39935;
  background: none;
  font-family: "Inter", "Helvetica Neue", Arial, sans-serif;
  font-size: 0.4em;
  font-weight: 700;
  letter-spacing: 0.2em;
  margin-left: 0.35em;
  opacity: 0.95;
  vertical-align: 0.15em;
}
@keyframes shimmer {
  0%, 100% { background-position: 0% 0%; }
  50%      { background-position: 100% 0%; }
}

/* ── Transitions ───────────────────────────────────────────────── */
.bid-enter-active { transition: all 0.6s cubic-bezier(0.22, 1, 0.36, 1); }
.bid-leave-active { transition: all 0.35s ease; }
.bid-enter-from   { opacity: 0; transform: translateY(-24px) scale(0.98); }
.bid-leave-to     { opacity: 0; transform: translateX(40px); }
</style>

<style>
.stronger { font-weight: bold; color: #c39935; }
</style>
